import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * WFP customization: fields the predictive search dropdown is allowed to match against.
 *
 * Shopify's default is 'title,product_type,variants.title,vendor'. Two consequences:
 *
 *  1. variants.sku is absent, so typing a SKU into the header search returned nothing even
 *     though the full /search results page found it.
 *  2. body is absent, and predictive search requires EVERY term in the query to match. So a
 *     single descriptive word the merchant never put in the title kills the whole dropdown.
 *     "tier large lion fountain with lion pedestals" returned "No results" because no title
 *     contains both "tier" and "pedestals" — while /search found the product first, because
 *     the /search endpoint searches body by default and predictive search does not.
 *
 * body is therefore the field that fixes the reported bug: descriptions carry the vocabulary
 * customers actually type ("four tier", "lower pedestals", "floating", "aerator"). Measured
 * across 42 realistic queries, adding it took dropdown zero-results from 20 to 3.
 *
 * 'tag' is excluded — measured on this store it changed no result set, because the tags are
 * filter plumbing (color:brown, brand:henri) duplicating what title/vendor already match.
 * 'variants.barcode' is excluded because no variant on this store has a barcode set.
 * @type {string}
 */
const PREDICTIVE_SEARCH_FIELDS = 'title,product_type,variants.title,vendor,variants.sku,body';

/**
 * WFP customization: make a multi-word query require ALL of its terms, without losing stemming.
 *
 * Bare multi-word queries on this store behave like an OR - "grande palazzo" returns 34 products
 * even though only 4 contain "palazzo" at all, which is the "why am I getting so many results"
 * complaint. Quoting a term forces Shopify to intersect the terms, so the first fix quoted every
 * term but the last. That did AND the query, but a quoted term is matched literally - stemming is
 * switched off - so the answer depended on which word happened to be typed last (measured on this
 * store, Sept 8 2026):
 *
 *   "lake" fountains    ->  83     "fountains" lake    ->  19
 *   "pool" waterfalls   ->  24     "waterfalls" pool   ->  10
 *   "outdoor" fountains -> 791     "fountains" outdoor -> 206
 *
 * A trailing wildcard forces the same intersection but leaves the term fuzzy, so the result set no
 * longer depends on word order (measured the same day, same endpoint):
 *
 *   lake* fountains*    ->  83     fountains* lake*    ->  83
 *   pool* waterfalls*   ->  26     waterfalls* pool*   ->  26
 *   outdoor* fountains* -> 792     fountains* outdoor* -> 792
 *   grande* palazzo*    ->   3     palazzo* grande*    ->   3
 *
 * "grande* palazzo*" still ranks the Henri Grande Palazzo first, and it also works mid-word, so the
 * dropdown keeps matching while the customer is still typing ("grande* pal*" finds it).
 *
 * Single-term queries are returned untouched, so single-word and SKU search ("FT-390") behave
 * exactly as they do today. Anything that already uses search syntax (quotes, field:value,
 * AND/OR/NOT, parentheses, wildcards, leading - or +) is also left alone, which makes this
 * idempotent.
 *
 * This same function runs for the predictive dropdown AND for the results page, so the dropdown
 * previews the set the results page delivers.
 *
 * @param {string} raw - the query the customer typed
 * @returns {string} the query to send to Shopify
 */
function wfpExpandSearchTerms(raw) {
  const query = (raw || '').trim();
  if (!query) return query;

  const terms = query.split(/\s+/);
  if (terms.length < 2) return query;

  const untouchable = terms.some(
    (term) =>
      /["():*~]/.test(term) ||
      term.includes(':') ||
      /^[-+]/.test(term) ||
      term === 'AND' ||
      term === 'OR' ||
      term === 'NOT'
  );
  if (untouchable) return query;

  return terms
    .map((term) => {
      // Trailing punctuation ("fountains," / "lake.") must not end up inside the quotes.
      const core = term.replace(/[^A-Za-z0-9À-ɏ]+$/, '');
      // Quote each term rather than wildcard it. As of 2026-09-11 Shopify's search no
      // longer intersects wildcard terms (nor the explicit AND operator): "lake* fountains*"
      // returned 926 of 980 products. Quoted terms still intersect, and Shopify still stems
      // inside quotes, so "cover" and "covers" both match. See docs/SOP-MASTER-PROMPT.md.
      return core ? '"' + core + '"' : term;
    })
    .join(' ');
}


/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    /**
     * WFP: the "View all" button in the modal footer is a bare <button> inside the form, so it
     * relied entirely on implicit form submission and had no handler of its own. Give it one,
     * and intercept the form submit too, so both routes to the results page go through the same
     * URL builder (AND-expanded query + type=product) instead of whatever the browser assembles.
     */
    const searchForm = this.querySelector('form.predictive-search-form');
    if (searchForm) searchForm.addEventListener('submit', this.#handleSearchSubmit, { signal });

    const viewAllButton = this.querySelector('.predictive-search__search-button');
    if (viewAllButton) viewAllButton.addEventListener('click', this.#handleViewAllClick, { signal });

    /**
     * WFP 2026-09-24: Horizon eagerly re-rendered the empty state here purely so it could
     * inject a "Recently viewed" row above the products. We show a curated collection in the
     * empty state instead (Theme settings > Search > empty state collection), which the section
     * already renders server-side, so there is nothing left to inject and no reason to re-fetch.
     */
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  /**
   * WFP 2026-09-24: was a second trigger for the recently-viewed injection. The server-rendered
   * empty state is already correct when the dialog opens, so this is now a no-op.
   */
  #handleDialogOpen = () => {};

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          // WFP: go through the shared builder so Enter and "View all" land on the same URL.
          const resultsUrl = this.#buildResultsUrl();
          if (resultsUrl) {
            event.preventDefault();
            window.location.assign(resultsUrl);
          }
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    // WFP: run the SAME AND expansion the results page uses. Without this the dropdown ran
    // the raw OR query, so "grande palazzo" previewed 10 cards (Grandessa, Granduer...) and
    // then "View all" landed on a results page with 3. Preview what gets delivered.
    url.searchParams.set('q', wfpExpandSearchTerms(searchTerm));
    url.searchParams.set('resources[limit_scope]', 'each');
    // WFP: Shopify's predictive search defaults to title,product_type,variants.title,vendor,
    // which means variant SKUs never match in the dropdown (the full /search page already
    // matches them). Widen the searched fields so typing a SKU surfaces the product.
    url.searchParams.set('resources[options][fields]', PREDICTIVE_SEARCH_FIELDS);

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * WFP: build the full search results URL for whatever is currently in the search box.
   * Keeps every field the form already carries (options[prefix]=last), pins type=product so the
   * reported result count matches the product grid the template actually renders, and runs the
   * query through wfpExpandSearchTerms so multi-word searches are ANDed.
   *
   * @param {HTMLFormElement | null} [form] - the search form, when the call came from it.
   * @returns {string | null} the URL to navigate to, or null if there is nothing to search for.
   */
  #buildResultsUrl = (form = null) => {
    const raw = (this.refs.searchInput?.value || '').trim();
    if (!raw) return null;

    try {
      const searchForm = form ?? this.querySelector('form.predictive-search-form');
      const params = new URLSearchParams(searchForm ? new FormData(searchForm) : undefined);
      params.set('q', wfpExpandSearchTerms(raw));
      params.set('type', 'product');

      const action =
        searchForm?.getAttribute('action') || globalThis.Theme?.routes?.search_url || '/search';

      return `${action}?${params.toString()}`;
    } catch (error) {
      return null;
    }
  };

  /**
   * WFP: "View all" click handler. Without this the button depended on implicit form submission.
   * @param {MouseEvent} event - The click event.
   */
  #handleViewAllClick = (event) => {
    const resultsUrl = this.#buildResultsUrl();
    if (!resultsUrl) return;

    event.preventDefault();
    window.location.assign(resultsUrl);
  };

  /**
   * WFP: catch every other way the form can be submitted (Enter with no results list rendered,
   * assistive tech, etc.) so those queries get the same treatment.
   * @param {SubmitEvent} event - The submit event.
   */
  #handleSearchSubmit = (event) => {
    const form = /** @type {HTMLFormElement} */ (event.currentTarget);
    const resultsUrl = this.#buildResultsUrl(form);
    if (!resultsUrl) return;

    event.preventDefault();
    window.location.assign(resultsUrl);
  };

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /**
     * WFP 2026-09-24: Horizon prepended a "Recently viewed" row here, built from localStorage.
     * Removed — the empty state now shows only the curated collection set in
     * Theme settings > Search, which `predictive-search-empty` already renders server-side.
     * Typed-query results are untouched; they still rank by relevance to the search term.
     */

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
